import { fullExtensions, useEditor } from '@modules/editor';
import {
  TableOfContentsItem,
  tableOfContentsExtension,
} from '@modules/editor/extensions/tableOfContents';
import { useMemo, useState } from 'react';
import { Doc } from 'src/@types/docs';
import { useAuthContext } from 'src/auth/useAuthContext';
import { useLocales } from 'src/locales';
import { useCollaboration } from '../../../../../features/collaboration/useCollaboration';
import { useDocAllowedAction } from '../../shared/useDocAllowedAction';
import { useDocMutation } from '../../shared/useDocMutation';
import './custom.css';
import { useDocItems } from '@modules/docs/providers';
import { collaborationExtension } from '@modules/editor/extensions/collaboration';
import { collaborationCursorExtension } from '@modules/editor/extensions/collaborationCursor';
import { mentionDocExtension } from '@modules/editor/extensions/mentionDoc';
import { userMentionExtension } from '@modules/editor/extensions/userMention';
import { useWorkspaceInfoContextStore } from '@modules/workspace/providers/WorkspaceInfo';
import { routes } from 'src/Router/paths';
import { useCollaborationContext } from 'src/features/collaboration';

export const useDocContentEditor = (doc: Doc) => {
  const { doc: ydoc, canRender, provider } = useCollaboration(`doc.${doc._id}`);
  const { user } = useAuthContext();
  const [tableOfContents, setTableOfContents] = useState<TableOfContentsItem[]>(
    [],
  );
  const { createDoc } = useDocMutation();
  const { translate } = useLocales();

  const { canEditDoc } = useDocAllowedAction({ doc });
  const workspaceStore = useWorkspaceInfoContextStore();

  const { awarenessColor } = useCollaborationContext();

  const { docsMap } = useDocItems();

  const extensions = useMemo(
    () => [
      ...fullExtensions,
      userMentionExtension,
      mentionDocExtension,

      tableOfContentsExtension.configure({
        onUpdate: (items) => {
          setTableOfContents(items);
        },
      }),
      ...(ydoc
        ? [
            collaborationExtension.configure({
              document: ydoc,
            }),
            collaborationCursorExtension.configure({
              provider,
              user: {
                name: user?.name || 'Fellow User',
                color: awarenessColor,
              },
            }),
          ]
        : []),
    ],
    [user, awarenessColor, ydoc, provider],
  );

  const { editor } = useEditor(
    {
      content: '',
      editable: canEditDoc,
      extensions,
      extensionConfig: {
        placeholder: translate('shared.editor.docs_placeholder'),
        enterBehave: {
          createNewLine: true,
        },
        aiPromptOptions: {
          enableSpaceForAi: true,
          position: 'sticky',
          currentDocument: doc,
          responseContainerHeight: '350px',
          container: 'editor',
        },
        userMention: {
          enabled: true,
          currentUserId: user?._id,
        },
        mentionDoc: {
          async onCreateNewDocument() {
            const newDoc = await createDoc({ parent: doc._id });
            return { id: newDoc._id };
          },
          getDocumentById(id) {
            const doc = docsMap?.[id];
            return {
              _id: id,
              title: doc?.title || translate('shared.labels.deleted'),
              icon: doc?.icon,
            };
          },
          getDocumentPath(doc) {
            return routes.dashboard.docs.view(doc._id);
          },
        },
      },
      externalStores: {
        workspaceStore,
      },
    },
    [extensions, user, docsMap, workspaceStore],
  );

  return { editor, canRender, tableOfContents };
};
